import i18next from 'i18next';
import React, { cloneElement, FC, PropsWithChildren, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../button';
import { Icon } from '../icon';
import { Modal, ModalProps } from '../modal';
import { Stack } from '../stack';
import { Typography } from '../typography';

export type ConfirmationModalActions = (props: {
  onCancel?: () => void;
  onConfirm?: () => void;
  onClose?: () => void;
  isLoading?: boolean;
}) => JSX.Element;

export type ConfirmationModalProps = PropsWithChildren & {
  /**
   * Open/Close state
   */
  open?: boolean;

  /**
   * Called, when the modal is cancelled
   */
  onCancel?: () => void;

  /**
   * Called as soon, as the user approves the action
   */
  onConfirm?: () => void;

  /**
   * Custom action title
   */
  title: string;

  /**
   * Optional loading state
   */
  isLoading?: boolean;

  /**
   * An optional custom confirm button component
   */
  confirmButton?: ReactElement;

  /**
   * Represents a function that returns a JSX element based on provided actions and state.
   */
  actions?: ConfirmationModalActions;

  /**
   * A callback function that is triggered when a closing action occurs.
   * This can be used to handle logic for closing components, such as modals or popups.
   * It is optional and does not take any parameters or return any value.
   */
  onClose?: () => void;

  /**
   * Represents the size property for a modal.
   *
   * This optional property determines the size of the modal, based on predefined size options available in ModalProps.
   * It can be used to customize the appearance or dimensions of the modal.
   */
  size?: ModalProps['size'];
};

export const ConfirmationModal: FC<ConfirmationModalProps> = ({
  open,
  isLoading = false,
  onConfirm,
  onCancel,
  title,
  children,
  actions,
  onClose,
  size,
  confirmButton = (
    <Button color="danger" prefix={<Icon icon={['fas', 'trash']} />}>
      {i18next.t<string>('common.clear')}
    </Button>
  ),
}) => {
  const [t] = useTranslation();

  return (
    <Modal
      size={size}
      open={open}
      onClose={onCancel}
      title={title}
      label={title}
      className="confirmation-modal-component"
      zIndex="confirmation-dialog"
      footer={(
        <Stack crossAxis="stretch">
          {actions?.({ onCancel, onConfirm, isLoading, onClose })}
          {!actions && (
            <>
              <Button
                color="dark-grey"
                variant="text"
                onClick={onCancel}
                autoFocus
                disabled={isLoading}
                data-testid="cancel"
              >
                {t('common.cancel')}
              </Button>
              {cloneElement(confirmButton, {
                'onClick': confirmButton.props.onClick ?? onConfirm,
                'data-testid': confirmButton.props['data-testid'] ?? 'confirm',
                'disabled': isLoading,
              })}
            </>
          )}
        </Stack>
      )}
    >
      <Stack fill direction="column" spacing={2}>
        {React.isValidElement(children) ? (
          children
        ) : (
          <Typography as="p">{children}</Typography>
        )}
      </Stack>
    </Modal>
  );
};
